import { api, postJson } from "../lib/api.js";
import { assetSearchPath, browserTimezone, localDateOnly } from "./helpers.js";
import { subscribeToLocalDay } from "./localDay.js";
import type {
  Account,
  Asset,
  AssetValueReport,
  AuditEvent,
  BmsOverview,
  Budget,
  Category,
  ChoreAnalytics,
  ChoreCategory,
  ChoreOccurrence,
  ChoreTemplate,
  ChoreView,
  FinanceOverview,
  HouseholdMember,
  InsuranceCoverageReport,
  Location,
  MealAssemblerResponse,
  NetworkOverview,
  MealPlanEntry,
  Product,
  Recipe,
  RecurringItem,
  ShoppingList,
  ShoppingTrip,
  StockLot,
  Store,
  Summary,
  TaskOccurrence,
  Transaction
} from "./types.js";

/**
 * What the things are worth, by category (#1187). Aggregated server-side — a
 * screen must not pull the whole inventory down to add it up.
 *
 * The reader's timezone rides the read (#1182): a depreciated book value steps
 * on the thing's monthly anniversary, so the server must judge it against the
 * reader's today or these totals disagree with the details panel beside them
 * for a few hours on exactly the day somebody would notice.
 *
 * Named rather than inlined at its two call sites because the day boundary has
 * to re-issue it without re-issuing the rest of the loader it sits in (#1469).
 */
export async function loadAssetValueReport() {
  return api<AssetValueReport>(`/api/v1/reports/asset-value?timezone=${encodeURIComponent(browserTimezone())}`);
}

/**
 * The other half of that question (#1211): what the policies say they cover.
 *
 * Beside the value read rather than inside it, because the two answer to
 * different features and only this one is empty for a household with no
 * policies on file. It is folded from the same effective values, so it goes
 * stale for the same reason and re-derives on the same tick.
 */
export async function loadInsuranceCoverageReport() {
  return api<InsuranceCoverageReport>(`/api/v1/reports/insurance-coverage?timezone=${encodeURIComponent(browserTimezone())}`);
}

function mergeAuditWithReleaseActivity(audit: AuditEvent[], releaseAudit: AuditEvent[]): AuditEvent[] {
  const auditIds = new Set(audit.map((event) => event.id));
  const missingReleaseEvents = releaseAudit.filter((event) => !auditIds.has(event.id));
  if (!missingReleaseEvents.length) return audit;
  return [...audit, ...missingReleaseEvents].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** One of the dashboard's reads, named so a panel can say which one failed. */
export type DashboardSection =
  | "summary"
  | "assetValue"
  | "insuranceCoverage"
  | "expirations"
  | "audit"
  | "taskOccurrences"
  | "shoppingLists";

/**
 * What one dashboard load produced. A section that failed is simply absent,
 * so the shell keeps whatever it already had for it, and is named in `failed`
 * so the panel that shows it can say the read did not come back.
 */
export type DashboardData = {
  summary?: Summary;
  assetValue?: AssetValueReport;
  insuranceCoverage?: InsuranceCoverageReport;
  expirations?: StockLot[];
  audit?: AuditEvent[];
  taskOccurrences?: TaskOccurrence[];
  shoppingLists?: ShoppingList[];
  failed: DashboardSection[];
};

export async function loadDashboardData(): Promise<DashboardData> {
  const today = localDateOnly();
  const timezone = browserTimezone();
  // The dashboard used to POST /task-occurrences/generate before these reads on
  // every single load — a write transaction serialized in front of the app's
  // home screen (#521). `generateThrough` asks the read itself to materialize
  // anything missing; the server decides with reads and writes only when an
  // occurrence is genuinely absent, so a steady-state load writes nothing.
  const taskQuery = new URLSearchParams({ view: "today_overdue", today, timezone, generateThrough: today }).toString();
  // Settled one by one, not `Promise.all` (September audit). All-or-nothing
  // meant one failed read threw the other seven away, and the dashboard then
  // painted its defaults — "0 Things", "Nothing urgent right now" — which are
  // indistinguishable from an empty household. Now each read lands on its own
  // and the ones that did not are named, so their panels can say so.
  const [summary, assetValue, insuranceCoverage, expirations, audit, releaseAudit, taskOccurrences, shoppingLists] = await Promise.allSettled([
    api<Summary>("/api/v1/reports/summary"),
    // The two date-derived aggregates, and the only reads in this loader that a
    // change of date alone can invalidate — which is why they are named
    // functions the day-boundary tick can re-issue on their own (#1469).
    loadAssetValueReport(),
    loadInsuranceCoverageReport(),
    api<StockLot[]>("/api/v1/expirations"),
    // Excluded server-side, before the query's limit: assistant telemetry can
    // fill a whole page on its own, so asking for the default 100 and filtering
    // here would leave the panel empty rather than showing older household
    // activity (#864). Only this feed opts in.
    api<AuditEvent[]>("/api/v1/audit?excludeAgentActivity=true"),
    api<AuditEvent[]>("/api/v1/audit?entityType=release_note&limit=1"),
    api<TaskOccurrence[]>(`/api/v1/task-occurrences?${taskQuery}`),
    api<ShoppingList[]>("/api/v1/shopping-lists")
  ]);
  const failed: DashboardSection[] = [];
  const take = <T,>(result: PromiseSettledResult<T>, section: DashboardSection): T | undefined => {
    if (result.status === "fulfilled") return result.value;
    failed.push(section);
    return undefined;
  };
  const data: DashboardData = { failed };
  const summaryValue = take(summary, "summary");
  if (summaryValue !== undefined) data.summary = summaryValue;
  const assetValueValue = take(assetValue, "assetValue");
  if (assetValueValue !== undefined) data.assetValue = assetValueValue;
  const coverageValue = take(insuranceCoverage, "insuranceCoverage");
  if (coverageValue !== undefined) data.insuranceCoverage = coverageValue;
  const expirationsValue = take(expirations, "expirations");
  if (expirationsValue !== undefined) data.expirations = expirationsValue;
  const auditValue = take(audit, "audit");
  // The release-note read only decorates the feed: if it alone fails, the feed
  // still shows the household's own activity.
  if (auditValue !== undefined) data.audit = mergeAuditWithReleaseActivity(auditValue, releaseAudit.status === "fulfilled" ? releaseAudit.value : []);
  const occurrencesValue = take(taskOccurrences, "taskOccurrences");
  if (occurrencesValue !== undefined) data.taskOccurrences = occurrencesValue;
  const listsValue = take(shoppingLists, "shoppingLists");
  if (listsValue !== undefined) data.shoppingLists = listsValue;
  return data;
}

export async function loadLocationsData() {
  return api<Location[]>("/api/v1/locations");
}

export async function loadAssetsData(query = "") {
  return api<Asset[]>(assetSearchPath(query));
}

export async function loadInventoryData() {
  const [locations, assets, assetValue] = await Promise.all([
    loadLocationsData(),
    loadAssetsData(),
    // The Things breakdown reads the same rollup the dashboard does, rather than
    // re-bucketing the loaded assets here: `assets` carries no template, so a
    // thing whose category lives only on its template would land in the wrong
    // bucket — and two implementations of one vocabulary is what #1187 set out
    // to avoid.
    loadAssetValueReport()
  ]);
  return { locations, assets, assetValue };
}

/**
 * Which screen's date-derived aggregates to re-read at the day boundary.
 *
 * Named for the loader they were pulled out of, not for the tab: `"dashboard"`
 * is the pair `loadDashboardData` folds a date into, `"inventory"` the single
 * rollup `loadInventoryData` does.
 */
export type DateDerivedScope = "dashboard" | "inventory";

export type DateDerivedAggregates = {
  assetValue: AssetValueReport;
  insuranceCoverage?: InsuranceCoverageReport;
};

/**
 * Re-read only the reads a change of date invalidates (#1469).
 *
 * `loadDashboardData` fetches eight things and `loadInventoryData` three; two
 * and one of those respectively fold the reader's date. Re-running the whole
 * loader at midnight would refetch shopping lists, audit rows and the summary
 * for nothing, so the boundary calls this instead — the same functions the
 * loaders call, so there is still one definition of each read.
 */
export async function loadDateDerivedAggregates(scope: DateDerivedScope): Promise<DateDerivedAggregates> {
  if (scope === "inventory") return { assetValue: await loadAssetValueReport() };
  const [assetValue, insuranceCoverage] = await Promise.all([
    loadAssetValueReport(),
    loadInsuranceCoverageReport()
  ]);
  return { assetValue, insuranceCoverage };
}

/**
 * Keep a screen's aggregate answers on the reader's own day. Returns an
 * unsubscribe.
 *
 * Three things this deliberately is not:
 *
 * - It is not a second day-boundary mechanism. It rides `subscribeToLocalDay`
 *   (#1450), the same signal the depreciation panel and the ages section on
 *   these very screens already ride. Two clocks is how the aggregate and the
 *   detail drift apart again, which is the disagreement #1187 and #1182 exist
 *   to prevent.
 * - It is not a shell load. A shell load is the path that shows the screen's
 *   loading state, and a boundary crossed at 00:00 with nobody touching
 *   anything is not a navigation: nothing is cleared, nothing goes busy, the
 *   figures are simply replaced when the answer arrives. Hence `apply` rather
 *   than a re-entry into the loader.
 * - It is not an error surface. A refresh nobody asked for must not put a
 *   banner over a screen that is still perfectly readable, so a failed tick is
 *   swallowed and the previous figures stand; the next tab entry, realtime
 *   echo or household event reloads them the normal way.
 */
export function subscribeToDateDerivedAggregates(
  scope: DateDerivedScope,
  apply: (next: DateDerivedAggregates) => void
): () => void {
  return subscribeToLocalDay(() => {
    void loadDateDerivedAggregates(scope).then(apply, () => undefined);
  });
}

export async function loadGroceryData() {
  const [locations, products, stockLots, expirations] = await Promise.all([
    loadLocationsData(),
    api<Product[]>("/api/v1/products"),
    api<StockLot[]>("/api/v1/stock-lots"),
    api<StockLot[]>("/api/v1/expirations")
  ]);
  return { locations, products, stockLots, expirations };
}

/**
 * The shell's recipe-adjacent state. The catalog itself is fetched only for the
 * Meals hub, whose planner searches and plans over every recipe (#1677): the
 * Recipes page fetches its own card-sized pages and never reads this list, so
 * `catalog: false` leaves the shell's copy untouched. Rows carry the compact
 * availability summary the Meals plan cards read; the per-ingredient list is
 * fetched from the detail route at the moment it is needed.
 */
export async function loadRecipeData(options: { catalog?: boolean } = {}) {
  const [recipes, mealPlanEntries, shoppingLists] = await Promise.all([
    options.catalog === false ? null : api<Recipe[]>("/api/v1/recipes?availability=1"),
    api<MealPlanEntry[]>("/api/v1/meal-plan"),
    api<ShoppingList[]>("/api/v1/shopping-lists")
  ]);
  return { recipes, mealPlanEntries, shoppingLists };
}

export async function loadMealAssemblerData(mealQuery: string, signal?: AbortSignal) {
  return api<MealAssemblerResponse>(`/api/v1/meal-assembler/suggestions?${mealQuery}`, { signal });
}

export async function loadShoppingData() {
  const [shoppingLists, shoppingTrips, stores] = await Promise.all([
    api<ShoppingList[]>("/api/v1/shopping-lists"),
    api<ShoppingTrip[]>("/api/v1/shopping-trips"),
    api<Store[]>("/api/v1/stores")
  ]);
  return { shoppingLists, shoppingTrips, stores };
}

/**
 * Deliberately NOT subscribed to the day boundary (#1469).
 *
 * `generateThrough` makes this read write: the server materializes any
 * occurrence in the window that has no row yet (#521). A midnight tick fires on
 * every open tab at once, so subscribing it would have every household member's
 * idle phone open a horizon-generation transaction at 00:00 — a write nobody
 * asked for, to answer a question nobody is asking, on the one read here that
 * cannot be made cheap. The value aggregates above have no such cost: they only
 * read.
 *
 * The reader loses nothing that was ever promised. This loader re-runs on tab
 * entry, on a chore-view change and on a realtime echo, so a reader who returns
 * to Chores after midnight gets the new day's window before they see anything;
 * only a tab parked on Chores across the boundary holds yesterday's list, and it
 * holds a list of individual rows, not a total contradicted by a panel beside
 * it. If that is later judged worth fixing it wants a read-only variant — the
 * same query with `generateThrough` omitted — rather than this one on a timer.
 */
export async function loadChoreData(view: ChoreView = "today", options: { seedDefaults?: boolean } = {}) {
  const today = localDateOnly();
  const timezone = browserTimezone();
  const throughDate = view === "upcoming_7" || view === "by_person" || view === "by_room" || view === "all"
    ? (() => {
        const date = new Date(`${today}T12:00:00`);
        date.setDate(date.getDate() + 7);
        return localDateOnly(date);
      })()
    : today;
  // Default categories are seeded at signup and only need a client-side backfill
  // once per session (for households created before that). Re-seeding on every
  // refresh just added a wasted round-trip to the hot chore-add path.
  if (options.seedDefaults) await postJson("/api/v1/chores/categories/seed", {});
  // Horizon materialization rides along with the occurrence read instead of a
  // POST in front of it (#521); the server only writes when a due date in the
  // window has no occurrence row yet.
  const query = new URLSearchParams({ view, today, timezone, generateThrough: throughDate }).toString();
  const [context, occurrences, templates] = await Promise.all([
    api<{ users: HouseholdMember[]; locations: Pick<Location, "id" | "name" | "type">[]; categories: ChoreCategory[] }>("/api/v1/chores/context"),
    api<ChoreOccurrence[]>(`/api/v1/chores/occurrences?${query}`),
    api<ChoreTemplate[]>("/api/v1/chores/templates")
  ]);
  // Analytics counts the same occurrence rows the list above materializes, so it
  // must not run beside that read: in the parallel form it usually won the race
  // and reported an overdue count and completion rate computed from the
  // pre-generation snapshot, disagreeing with the list rendered next to it. The
  // old code got this ordering for free from the awaited generate POST. Round
  // trips are unchanged — two serial steps then as now, minus the write.
  const analytics = await api<ChoreAnalytics>(`/api/v1/chores/analytics?${new URLSearchParams({ today, timezone }).toString()}`);
  return { ...context, occurrences, templates, analytics };
}

export async function loadBmsData() {
  return api<BmsOverview>("/api/v1/bms/overview");
}

export async function loadNetworkData() {
  return api<NetworkOverview>("/api/v1/network/overview");
}

export async function loadFinanceData() {
  const today = localDateOnly();
  const month = today.slice(0, 7);
  const periodQuery = new URLSearchParams({ today, month }).toString();
  const [accounts, categories, transactions, budgets, recurringItems, overview] = await Promise.all([
    api<Account[]>("/api/v1/finance/accounts"),
    api<Category[]>("/api/v1/finance/categories"),
    api<Transaction[]>("/api/v1/finance/transactions"),
    api<Budget[]>(`/api/v1/finance/budgets?${periodQuery}`),
    api<RecurringItem[]>("/api/v1/finance/recurring"),
    api<FinanceOverview>(`/api/v1/finance/overview?${periodQuery}`)
  ]);
  return { accounts, categories, transactions, budgets, recurringItems, overview };
}
